import TvIcon from '@mui/icons-material/Tv';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { claimScreen, useGetInstances } from 'gen/client';
import { InstanceDTO } from 'gen/model';
import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function ScreenRegister() {
    const [params] = useSearchParams();
    const { data: instances = [], error, isLoading } = useGetInstances();
    const [selectedInstance, setSelectedInstance] = useState<InstanceDTO | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const screenId = params.get('id');

    const onConfirm = useCallback(async () => {
        if (!selectedInstance || !screenId) {
            return;
        }

        try {
            setSubmitting(true);
            setErrorMessage(null);

            await claimScreen({
                installationId: screenId,
                instance: selectedInstance.id,
            });

            window.open(selectedInstance.domain + '/admin/devices/infoscreen?id=' + screenId, '_self');
        } catch (error) {
            console.error('Error claiming screen:', error);
            setErrorMessage('Failed to register screen. Please try again.');
        } finally {
            setSubmitting(false);
        }
    }, [screenId, selectedInstance]);

    if (!screenId) {
        return (
            <Container
                maxWidth="sm"
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    py: { sm: 6, xs: 4 },
                }}
            >
                <Paper
                    sx={{
                        borderRadius: 2,
                        boxShadow: 3,
                        maxWidth: '100%',
                        p: 3,
                        width: '100%',
                    }}
                >
                    <Stack
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                alignItems: 'center',
                                bgcolor: 'error.main',
                                borderRadius: '50%',
                                display: 'flex',
                                justifyContent: 'center',
                                mb: 2,
                                p: 2,
                            }}
                        >
                            <TvIcon sx={{ color: 'white', fontSize: 40 }} />
                        </Box>
                        <Typography sx={{ fontWeight: 'medium', mb: 1 }} variant="h5">
                            Screen Registration
                        </Typography>
                        <Alert severity="error" sx={{ width: '100%' }}>
                            Screen ID not found in URL
                        </Alert>
                        <Typography align="center">Please scan a valid QR code to register your screen.</Typography>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    return (
        <Container
            maxWidth="sm"
            sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
            }}
        >
            <Paper
                sx={{
                    borderRadius: 2,
                    boxShadow: 3,
                    maxWidth: '100%',
                    p: { sm: 4, xs: 2 },
                    width: '100%',
                }}
            >
                <Stack
                    spacing={3}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            alignItems: 'center',
                            bgcolor: 'primary.main',
                            borderRadius: '50%',
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 1,
                            p: 2,
                        }}
                    >
                        <TvIcon sx={{ color: 'white', fontSize: 40 }} />
                    </Box>

                    <Typography sx={{ fontWeight: 'medium' }} variant="h5">
                        Register New Screen
                    </Typography>

                    <Divider sx={{ width: '100%' }} />

                    <Typography sx={{ mb: 1, textAlign: 'center' }}>
                        Please select the location where this screen will be installed:
                    </Typography>

                    {isLoading ? (
                        <CircularProgress size={24} />
                    ) : error ? (
                        <Alert severity="error" sx={{ width: '100%' }}>
                            Error loading locations. Please refresh the page.
                        </Alert>
                    ) : (
                        <>
                            <Autocomplete
                                getOptionLabel={(option: InstanceDTO) => option.name || option.id}
                                onChange={(_: React.SyntheticEvent, newValue: InstanceDTO | null) =>
                                    setSelectedInstance(newValue)
                                }
                                options={instances}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        helperText="Choose from available locations"
                                        label="Select Location"
                                        size="medium"
                                    />
                                )}
                                sx={{ width: '100%' }}
                                value={selectedInstance}
                            />

                            {errorMessage ? (
                                <Alert severity="error" sx={{ width: '100%' }}>
                                    {errorMessage}
                                </Alert>
                            ) : null}

                            <Button
                                disabled={!selectedInstance || submitting}
                                onClick={onConfirm}
                                size="large"
                                sx={{
                                    fontSize: '1rem',
                                    mt: 1,
                                    position: 'relative',
                                    px: 4,
                                    py: 1.5,
                                    width: { sm: 'auto', xs: '100%' },
                                }}
                                variant="contained"
                            >
                                Confirm
                                {submitting ? (
                                    <CircularProgress
                                        size={20}
                                        sx={{
                                            color: 'white',
                                            position: 'absolute',
                                            right: 12,
                                        }}
                                    />
                                ) : null}
                            </Button>

                            <Typography
                                sx={{
                                    color: 'text.secondary',
                                    textAlign: 'center',
                                }}
                                variant="caption"
                            >
                                After confirmation, you will be redirected to configure additional settings
                            </Typography>
                        </>
                    )}
                </Stack>
            </Paper>
        </Container>
    );
}
